"use client";

import { useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import { ORDER_JOURNEY_FILTER } from "@convex/constants/orderJourney.lib";

export const ACTION_JOURNEYS_ARGS = {
  filter: ORDER_JOURNEY_FILTER.NEEDS_ACTION,
  limit: 10,
};

export interface ActivityFeedData {
  vendorActionItems?: FunctionReturnType<typeof api.dashboards.actionItems.getActionItems>;
  actionJourneys?: FunctionReturnType<typeof api.orders.journeys.listMine>;
  carrierCounts?: FunctionReturnType<typeof api.freight.carrierDashboard.getCarrierNavCounts>;
  companyReadiness?: FunctionReturnType<typeof api.profileCompletion.queries.getCompanyReadiness>;
  ticketsAwaiting?: FunctionReturnType<typeof api.support.queries.listMyTicketsAwaitingResponse>;
}

interface ActivityFeedAudience {
  enabled: boolean;
  isVendor: boolean;
  isBuyer: boolean;
  isFreight: boolean;
  companyId: Id<"companies"> | undefined;
}

export function useActivityFeedData(
  { enabled, isVendor, isBuyer, isFreight, companyId }: ActivityFeedAudience,
): ActivityFeedData {
  const vendorActionItems = useQuery(
    api.dashboards.actionItems.getActionItems,
    enabled && isVendor && companyId ? { companyId } : "skip",
  );
  const actionJourneys = useQuery(
    api.orders.journeys.listMine,
    enabled && (isBuyer || isVendor) ? ACTION_JOURNEYS_ARGS : "skip",
  );
  const carrierCounts = useQuery(
    api.freight.carrierDashboard.getCarrierNavCounts,
    enabled && isFreight ? {} : "skip",
  );
  const companyReadiness = useQuery(
    api.profileCompletion.queries.getCompanyReadiness,
    enabled && (isVendor || isBuyer) ? {} : "skip",
  );
  const ticketsAwaiting = useQuery(
    api.support.queries.listMyTicketsAwaitingResponse,
    enabled ? {} : "skip",
  );
  return { vendorActionItems, actionJourneys, carrierCounts, companyReadiness, ticketsAwaiting };
}

export function activityFeedDataLoading(
  { isVendor, isBuyer, isFreight }: Pick<ActivityFeedAudience, "isVendor" | "isBuyer" | "isFreight">,
  data: ActivityFeedData,
): boolean {
  return ((isVendor || isBuyer) && data.companyReadiness === undefined) ||
    data.ticketsAwaiting === undefined ||
    (isVendor && data.vendorActionItems === undefined) ||
    ((isBuyer || isVendor) && data.actionJourneys === undefined) ||
    (isFreight && data.carrierCounts === undefined);
}

function currentValue<Value>(live: Value | undefined, seed: Value | undefined): Value | undefined {
  return live === undefined ? seed : live;
}

export function initialActivityFeedData(live: ActivityFeedData, seed?: ActivityFeedData): ActivityFeedData {
  return {
    vendorActionItems: currentValue(live.vendorActionItems, seed?.vendorActionItems),
    actionJourneys: currentValue(live.actionJourneys, seed?.actionJourneys),
    carrierCounts: currentValue(live.carrierCounts, seed?.carrierCounts),
    companyReadiness: currentValue(live.companyReadiness, seed?.companyReadiness),
    ticketsAwaiting: currentValue(live.ticketsAwaiting, seed?.ticketsAwaiting),
  };
}
